import React from 'react';
import styled from '@emotion/styled';
import { format } from 'date-fns';
import { Tooltip } from '@mui/material';

// Create styled components for the delta and real-time bubbles
const DeltaBubble = styled('span')`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  font-size: 0.875rem;
  font-weight: 700;
  border-radius: 16px;
  color: black;
  background-color: ${(props) => (props.isNegative ? 'lightcoral' : 'lightgreen')};
  margin-left: 8px;
`;

const RealTimeBubble = styled('span')`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  font-size: 0.875rem;
  font-weight: 700;
  border-radius: 16px;
  color: black;
  background-color: red;
  margin-left: 8px;
`;

const adcColumns = ({ viewBotFilteredResults, viewRealTimeMetrics }) => {
    const today = format(new Date(), 'yyyy-MM-dd');

    const fieldMap = {
        visits_count: "visits_count_bot_filtered",
        visits_resolved_count: "visits_resolved_count_bot_filtered",
        distinct_visits_count: "distinct_visits_count_bot_filtered",
        distinct_visits_resolved_count: "distinct_visits_resolved_count_bot_filtered",
        distinct_hems_count: "distinct_hems_count_bot_filtered",
        percent_resolved: "percent_resolved_bot_filtered",
        unique_percent_resolved: "unique_percent_resolved_bot_filtered",
    };

    const realTimeFieldMap = {
        visits_resolved_count: "visits_resolved_count_real_time",
        distinct_visits_resolved_count: "distinct_visits_resolved_count_real_time",
        visits_resolved_count_bot_filtered: "visits_resolved_count_bot_filtered_real_time",
        distinct_visits_resolved_count_bot_filtered: "distinct_visits_resolved_count_bot_filtered_real_time",
    };

    const getOriginalField = (botFilteredField) => {
        // Reverse lookup: find the key in fieldMap that corresponds to the given value
        return Object.keys(fieldMap).find(key => fieldMap[key] === botFilteredField) || botFilteredField;
    };

    const renderCellWithDelta = (params, field) => {
        //console.log('params:', params);
        // When bot filtering is active, get the original field to calculate deltas correctly
        const originalField = viewBotFilteredResults ? getOriginalField(field) : field;
        const botFilteredField = fieldMap[originalField];

        //console.log('originalField:', originalField);
        //console.log('botFilteredField:', botFilteredField);

        const originalValue = params.row[originalField]; // Non-bot filtered value
        const botFilteredValue = params.row[botFilteredField]; // Bot filtered value
        //console.log('originalValue:', originalValue);
        //console.log('botFilteredValue:', botFilteredValue);

        const displayedValue = viewBotFilteredResults ? botFilteredValue : originalValue;
        const formattedDisplayedValue = displayedValue !== undefined ? new Intl.NumberFormat().format(displayedValue) : 'N/A';

        // Calculate deltaValue as the difference between botFilteredValue and originalValue
        const deltaValue = botFilteredValue !== undefined && originalValue !== undefined
            ? originalValue - botFilteredValue
            : undefined;

        const realTimeValue = params.row[realTimeFieldMap[originalField]];

        // Format the delta value
        const formattedDeltaValue = deltaValue !== undefined && !Number.isNaN(deltaValue)
            ? `${deltaValue >= 0 ? '-' : ''}${new Intl.NumberFormat().format(deltaValue)}`
            : null;

        // Render the cell content
        return (
            <span>
                {formattedDisplayedValue}
                {viewBotFilteredResults && formattedDeltaValue && (
                    <DeltaBubble isNegative={deltaValue < 0}>{formattedDeltaValue}</DeltaBubble>
                )}
                {viewRealTimeMetrics && realTimeValue !== undefined && (
                    <RealTimeBubble>{new Intl.NumberFormat().format(realTimeValue)}</RealTimeBubble>
                )}
            </span>
        );
    };

    const renderPercentResolved = (params) => {
        const visitsResolvedField = viewBotFilteredResults ? fieldMap.visits_resolved_count : "visits_resolved_count";
        const visitsCountField = viewBotFilteredResults ? fieldMap.visits_count : "visits_count";

        const visitsResolvedCount = params.row[visitsResolvedField];
        const visitsCount = params.row[visitsCountField];

        if (!visitsResolvedCount || !visitsCount) {
            return '0.00%';
        }

        const percentResolved = (visitsResolvedCount / visitsCount * 100).toFixed(2);
        const realTimePercentResolved = viewRealTimeMetrics && params.row[realTimeFieldMap.visits_resolved_count]
            ? (params.row[realTimeFieldMap.visits_resolved_count] / visitsCount * 100).toFixed(2)
            : null;

        // Calculate delta
        const originalPercentResolved = (params.row[getOriginalField(visitsResolvedField)] / params.row[getOriginalField(visitsCountField)] * 100).toFixed(2);
        const deltaPercent = (percentResolved - originalPercentResolved).toFixed(2);

        return (
            <span>
                {`${percentResolved}%`}
                {viewBotFilteredResults && deltaPercent !== '0.00' && (
                    <DeltaBubble isNegative={parseFloat(deltaPercent) < 0}>{`${deltaPercent >= 0 ? '+' : ''}${deltaPercent}%`}</DeltaBubble>
                )}
                {viewRealTimeMetrics && realTimePercentResolved !== null && (
                    <RealTimeBubble>{`${realTimePercentResolved}%`}</RealTimeBubble>
                )}
            </span>
        );
    };

    const renderUniquePercentResolved = (params) => {
        if (params.row.on_date === today) {
            return (
                <Tooltip title="Data not available for the current day as the counts aren't accurate yet.">
                    <span>Not Available</span>
                </Tooltip>
            );
        }

        const distinctVisitsResolvedField = viewBotFilteredResults ? fieldMap.distinct_visits_resolved_count : "distinct_visits_resolved_count";
        const distinctVisitsCountField = viewBotFilteredResults ? fieldMap.distinct_visits_count : "distinct_visits_count";

        const distinctVisitsResolvedCount = params.row[distinctVisitsResolvedField];
        const distinctVisitsCount = params.row[distinctVisitsCountField];

        if (!distinctVisitsResolvedCount || !distinctVisitsCount) {
            return '0.00%';
        }

        const uniquePercentResolved = (distinctVisitsResolvedCount / distinctVisitsCount * 100).toFixed(2);
        const realTimeUniquePercentResolved = viewRealTimeMetrics && params.row[realTimeFieldMap.distinct_visits_resolved_count]
            ? (params.row[realTimeFieldMap.distinct_visits_resolved_count] / distinctVisitsCount * 100).toFixed(2)
            : null;

        // Calculate delta
        const originalUniquePercentResolved = (params.row[getOriginalField(distinctVisitsResolvedField)] / params.row[getOriginalField(distinctVisitsCountField)] * 100).toFixed(2);
        const deltaUniquePercent = (uniquePercentResolved - originalUniquePercentResolved).toFixed(2);

        return (
            <span>
                {`${uniquePercentResolved}%`}
                {viewBotFilteredResults && deltaUniquePercent !== '0.00' && (
                    <DeltaBubble isNegative={parseFloat(deltaUniquePercent) < 0}>{`${deltaUniquePercent >= 0 ? '+' : ''}${deltaUniquePercent}%`}</DeltaBubble>
                )}
                {viewRealTimeMetrics && realTimeUniquePercentResolved !== null && (
                    <RealTimeBubble>{`${realTimeUniquePercentResolved}%`}</RealTimeBubble>
                )}
            </span>
        );
    };
    const columns = [
        {
            field: "on_date",
            headerName: "Date",
            width: 150,
            type: "date",
        },
        {
            field: getOriginalField("visits_count"),
            headerName: viewBotFilteredResults ? "Visits Count (No Bots)" : "Visits Count",
            width: 150,
            renderCell: (params) => renderCellWithDelta(params, "visits_count"),
        },
        {
            field: getOriginalField("visits_resolved_count"),
            headerName: viewBotFilteredResults ? "Visits Resolved Count (No Bots)" : "Visits Resolved Count",
            width: 200,
            renderCell: (params) => renderCellWithDelta(params, "visits_resolved_count"),
        },
        {
            field: getOriginalField("distinct_visits_count"),
            headerName: viewBotFilteredResults ? "Distinct Visits Count (No Bots)" : "Distinct Visits Count",
            width: 200,
            renderCell: (params) => renderCellWithDelta(params, "distinct_visits_count"),
        },
        {
            field: getOriginalField("distinct_visits_resolved_count"),
            headerName: viewBotFilteredResults ? "Distinct Visits Resolved Count (No Bots)" : "Distinct Visits Resolved Count",
            width: 250,
            renderCell: (params) => renderCellWithDelta(params, "distinct_visits_resolved_count"),
        },
        {
            field: getOriginalField("distinct_hems_count"),
            headerName: viewBotFilteredResults ? "Distinct HEMs Delivered (No Bots)" : "Distinct HEMs Delivered",
            width: 200,
            renderCell: (params) => renderCellWithDelta(params, "distinct_hems_count"),
        },
        {
            field: "percent_resolved",
            headerName: viewBotFilteredResults ? "% Resolved (No Bots)" : "% Resolved",
            width: 150,
            renderCell: (params) => renderPercentResolved(params),
        },
        {
            field: "unique_percent_resolved",
            headerName: viewBotFilteredResults ? "Unique % Resolved (No Bots)" : "Unique % Resolved",
            width: 200,
            renderCell: (params) => renderUniquePercentResolved(params),
        },
    ];

    return columns;
};

export default adcColumns;
